import { useMemo, type MouseEvent, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { isApiError } from '@dorixona/api'
import { resolveLanguage, type Language } from '@dorixona/i18n'
import { Button } from '@dorixona/ui'
import { formatDay, isoString } from '@dorixona/utils'
import { openOutside } from '../../../app/telegram'
import { BackButton } from '../../../ui/BackButton'
import { Icon } from '../../../ui/Icon'
import { Failed } from '../../../ui/Load'
import { sanitizeHtml } from '../../legal/sanitize'
import { env } from '../../../config/env'
import { useNews } from '../api'
import { internalPath, resolve, type News } from '../model'
import { NewsPhoto } from './NewsPhoto'

const EXTERNAL = /^https?:\/\//i

/**
 * Экран новости: фото, дата, заголовок и текст из редактора админки. Текст
 * попадает на страницу только через фильтр (legal/sanitize.ts). Новости нет
 * или её сняли с публикации — экран так и говорит и ведёт к ленте.
 */
export function NewsScreen() {
  const { t, i18n } = useTranslation(['home', 'common'])
  const language = resolveLanguage([i18n.language])
  const { newsId = '' } = useParams()
  const id = /^\d+$/.test(newsId) ? Number(newsId) : 0
  const news = useNews(id)

  let content: ReactNode
  if (id === 0 || (isApiError(news.error) && news.error.code === 'not_found')) {
    content = <NewsNotFound />
  } else if (news.isSuccess) {
    content = <NewsArticle news={news.data} language={language} />
  } else if (news.isError) {
    content = (
      <Failed
        error={news.error}
        onRetry={() => {
          void news.refetch()
        }}
      />
    )
  } else {
    content = (
      <div className="news-article" role="status" aria-busy="true">
        <span className="visually-hidden">{t('common:state.loading')}</span>
        <span className="news-article__sk" aria-hidden="true">
          <i className="sk-b news-photo news-article__photo" />
          <i className="sk-b sk-l sk-s" />
          <i className="sk-b sk-l news-sk-title" />
          <i className="sk-b sk-l" />
          <i className="sk-b sk-l" />
          <i className="sk-b sk-l sk-s" />
        </span>
      </div>
    )
  }

  return (
    <section className="screen news-screen">
      <div className="mhead">
        <BackButton to="/news" />
      </div>
      {content}
    </section>
  )
}

/**
 * Заголовок и текст несут свой `lang`: узбекского текста может не быть, и
 * тогда показан русский — читалка экрана должна произносить его по-русски.
 */
function NewsArticle({ news, language }: { news: News; language: Language }) {
  const title = resolve(news.title, language)
  const body = resolve(news.body, language)
  return (
    <article className="news-article">
      <NewsPhoto src={news.photo} className="news-article__photo" loading="eager" />
      {news.publishedAt > 0 ? (
        <time className="news-article__date" dateTime={isoString(news.publishedAt)}>
          {formatDay(news.publishedAt)}
        </time>
      ) : null}
      <h1 className="news-article__title" lang={title.lang}>
        {title.text}
      </h1>
      <NewsBody html={body.text} lang={body.lang} />
    </article>
  )
}

/**
 * Текст новости. Ссылки внутри HTML — не наши компоненты, поэтому нажатие
 * ловит один обработчик на весь текст: путь приложения (`/stores`) открываем
 * роутером, без перезагрузки; чужой сайт — не теряя приложение (в Telegram его
 * браузером, во Flutter — через мост, на сайте — новой вкладкой). Почта и
 * телефон работают обычной ссылкой. Сам контейнер не интерактивный — Enter на
 * ссылке приходит сюда тем же click.
 */
function NewsBody({ html, lang }: { html: string; lang: Language }) {
  const navigate = useNavigate()
  const clean = useMemo(
    () => sanitizeHtml(html, { internalLinks: true, mediaBase: env.apiBaseUrl }),
    [html],
  )

  const openLink = (event: MouseEvent<HTMLDivElement>) => {
    const link = (event.target as Element).closest<HTMLAnchorElement>('a[href]')
    if (!link) return
    const href = link.getAttribute('href') as string
    const path = internalPath(href)
    if (path) {
      event.preventDefault()
      void navigate(path)
    } else if (EXTERNAL.test(href)) {
      event.preventDefault()
      openOutside(link.href)
    }
  }

  return (
    <div
      className="news-body"
      lang={lang}
      role="presentation"
      onClick={openLink}
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  )
}

function NewsNotFound() {
  const { t } = useTranslation(['home', 'common'])
  const navigate = useNavigate()
  return (
    <div className="card empty">
      <Icon name="i-search" size={54} />
      <h1 className="t">{t('home:news.notFound')}</h1>
      {t('home:news.notFoundHint')}
      <div className="news-empty-act">
        <Button
          onPress={() => {
            void navigate('/news')
          }}
        >
          {t('home:news.toList')}
        </Button>
      </div>
    </div>
  )
}
